import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import AdminLayout from '@/layouts/AdminLayout';
import { useAuthState } from '@/hooks/useAuthState';
import { useToast } from '@/hooks/use-toast';
import { useContactNotifications } from '@/hooks/useContactNotifications';
import { ContactService, ContactMessage } from '@/services/contactService';
import { 
  Mail, 
  Search, 
  Eye, 
  Trash2, 
  CheckCircle,
  XCircle,
  ChevronLeft,
  ChevronRight,
  Calendar,
  User,
  Phone,
  MessageSquare,
  MoreHorizontal
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const AdminContacts = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAuthenticated, userType, userName, userPhoto, userId, loading: authLoading } = useAuthState();
  const { refresh: refreshContactNotifications } = useContactNotifications();
  const [contacts, setContacts] = useState<ContactMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filterRead, setFilterRead] = useState('todos');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedContact, setSelectedContact] = useState<ContactMessage | null>(null);
  const [showViewDialog, setShowViewDialog] = useState(false);

  // Verificar se é admin (AdminLayout já faz isso)

  useEffect(() => {
    loadContacts();
  }, [currentPage, filterRead, searchTerm]);

  const loadContacts = async () => {
    try {
      setLoading(true);
      const allContacts = await ContactService.getAllContacts();

      // Aplicar filtros
      let filteredContacts = allContacts;
      
      // Filtro por termo de busca
      if (searchTerm) {
        filteredContacts = filteredContacts.filter(contact => 
          contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          contact.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (contact.phone && contact.phone.toLowerCase().includes(searchTerm.toLowerCase()))
        );
      }

      // Filtro por status de leitura
      if (filterRead !== 'todos') {
        filteredContacts = filteredContacts.filter(contact => {
          if (filterRead === 'lidas') return contact.is_read;
          if (filterRead === 'nao-lidas') return !contact.is_read;
          return true;
        });
      }

      setContacts(filteredContacts);
    } catch (error) {
      console.error('Erro ao carregar contatos:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os contatos",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleViewContact = async (contact: ContactMessage) => {
    setSelectedContact(contact);
    setShowViewDialog(true);
    
    // Marcar como lida se ainda não estiver lida
    if (!contact.is_read) {
      await handleToggleRead(contact.id, true);
    }
  };

  const handleToggleRead = async (contactId: number, isRead: boolean) => {
    try {
      await ContactService.toggleReadStatus(contactId, isRead);
      
      // Atualizar estado local imediatamente
      setContacts(prevContacts => 
        prevContacts.map(contact => 
          contact.id === contactId 
            ? { ...contact, is_read: isRead }
            : contact
        )
      );
      
      // Atualizar contato selecionado se for o mesmo
      if (selectedContact && selectedContact.id === contactId) {
        setSelectedContact({ ...selectedContact, is_read: isRead });
      }
      
      // Atualizar contador no header
      refreshContactNotifications();
      
      toast({
        title: "Sucesso",
        description: isRead ? "Mensagem marcada como lida" : "Mensagem marcada como não lida",
      });
      
      // Recarregar contatos para garantir sincronização
      await loadContacts();
      
      // Atualizar contador novamente após recarregar
      refreshContactNotifications();
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      toast({
        title: "Erro",
        description: "Não foi possível atualizar o status",
        variant: "destructive",
      });
    }
  };

  const handleDeleteContact = async (contactId: number) => {
    if (!confirm('Tem certeza que deseja excluir esta mensagem?')) {
      return;
    }

    try {
      await ContactService.deleteContact(contactId);
      
      // Atualizar estado local imediatamente
      setContacts(prevContacts => prevContacts.filter(contact => contact.id !== contactId));
      
      // Atualizar contador no header
      refreshContactNotifications();
      
      toast({
        title: "Sucesso",
        description: "Mensagem excluída com sucesso",
      });
      
      if (selectedContact && selectedContact.id === contactId) {
        setShowViewDialog(false);
        setSelectedContact(null);
      }
      
      // Recarregar contatos para garantir sincronização
      await loadContacts();
      
      // Atualizar contador novamente após recarregar
      refreshContactNotifications();
    } catch (error) {
      console.error('Erro ao excluir contato:', error);
      toast({
        title: "Erro",
        description: "Não foi possível excluir a mensagem",
        variant: "destructive",
      });
    }
  };

  // Paginação
  const totalPages = Math.ceil(contacts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentContacts = contacts.slice(startIndex, endIndex);

  const unreadCount = contacts.filter(c => !c.is_read).length;

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando contatos...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Mensagens de Contato</h1>
              <p className="text-muted-foreground">
                Gerencie todas as mensagens recebidas através do formulário "Fale Conosco"
              </p>
            </div>
          </div>

          {/* Estatísticas */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total de Mensagens</p>
                    <p className="text-2xl font-bold">{contacts.length}</p>
                  </div>
                  <Mail className="h-8 w-8 text-primary" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Não Lidas</p>
                    <p className="text-2xl font-bold text-primary">{unreadCount}</p>
                  </div>
                  <XCircle className="h-8 w-8 text-primary" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Lidas</p>
                    <p className="text-2xl font-bold text-green-600">{contacts.length - unreadCount}</p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filtros e Busca */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1 flex gap-2">
              <Input
                placeholder="Buscar por nome, e-mail, assunto..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleKeyPress}
                className="flex-1"
              />
              <Button onClick={handleSearch} variant="outline">
                <Search className="h-4 w-4 mr-2" />
                Buscar
              </Button>
              {searchTerm && (
                <Button onClick={handleClearSearch} variant="outline">
                  Limpar
                </Button>
              )}
            </div>
            <Select value={filterRead} onValueChange={setFilterRead}>
              <SelectTrigger className="w-full md:w-[200px]">
                <SelectValue placeholder="Filtrar por status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="nao-lidas">Não Lidas</SelectItem>
                <SelectItem value="lidas">Lidas</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Tabela de Contatos */}
        <Card>
          <CardHeader>
            <CardTitle>Lista de Mensagens</CardTitle>
          </CardHeader>
          <CardContent>
            {contacts.length === 0 ? (
              <div className="text-center py-12">
                <Mail className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Nenhuma mensagem encontrada</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Status</TableHead>
                        <TableHead>Nome</TableHead>
                        <TableHead>E-mail</TableHead>
                        <TableHead>Telefone</TableHead>
                        <TableHead>Assunto</TableHead>
                        <TableHead>Data</TableHead>
                        <TableHead className="text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {currentContacts.map((contact) => (
                        <TableRow key={contact.id} className={!contact.is_read ? 'bg-primary/5' : ''}>
                          <TableCell>
                            {contact.is_read ? (
                              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Lida
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-primary/10 text-primary border-primary">
                                <XCircle className="h-3 w-3 mr-1" />
                                Não Lida
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="font-medium">{contact.name}</TableCell>
                          <TableCell>{contact.email}</TableCell>
                          <TableCell>{contact.phone || '-'}</TableCell>
                          <TableCell className="max-w-[200px] truncate">{contact.subject}</TableCell>
                          <TableCell>
                            {format(new Date(contact.created_at), 'dd/MM/yyyy', { locale: ptBR })}
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleViewContact(contact)}>
                                  <Eye className="h-4 w-4 mr-2" />
                                  Visualizar
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => handleToggleRead(contact.id, !contact.is_read)}
                                >
                                  {contact.is_read ? (
                                    <>
                                      <XCircle className="h-4 w-4 mr-2" />
                                      Marcar como Não Lida
                                    </>
                                  ) : (
                                    <>
                                      <CheckCircle className="h-4 w-4 mr-2" />
                                      Marcar como Lida
                                    </>
                                  )}
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => handleDeleteContact(contact.id)}
                                  className="text-destructive"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Excluir
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Paginação */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <div className="text-sm text-muted-foreground">
                      Página {currentPage} de {totalPages}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Anterior
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                      >
                        Próxima
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dialog de Visualização */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Mensagem de {selectedContact?.name}
            </DialogTitle>
          </DialogHeader>
          {selectedContact && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Nome</p>
                  <p className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    {selectedContact.name}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">E-mail</p>
                  <p className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    <a href={`mailto:${selectedContact.email}`} className="text-primary hover:underline">
                      {selectedContact.email}
                    </a>
                  </p>
                </div>
                {selectedContact.phone && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Telefone</p>
                    <p className="flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      <a href={`tel:${selectedContact.phone}`} className="text-primary hover:underline">
                        {selectedContact.phone}
                      </a>
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Data</p>
                  <p className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {format(new Date(selectedContact.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                  </p>
                </div>
              </div>
              
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Assunto</p>
                <p className="font-semibold">{selectedContact.subject}</p>
              </div>
              
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Mensagem</p>
                <div className="bg-muted/50 p-4 rounded-lg">
                  <p className="whitespace-pre-wrap">{selectedContact.message}</p>
                </div>
              </div>

              {selectedContact.read_by_name && (
                <div className="text-sm text-muted-foreground">
                  Lida por: {selectedContact.read_by_name} em {selectedContact.read_at && format(new Date(selectedContact.read_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                </div>
              )}

              <div className="flex gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => handleToggleRead(selectedContact.id, !selectedContact.is_read)}
                  className="flex-1"
                >
                  {selectedContact.is_read ? (
                    <>
                      <XCircle className="h-4 w-4 mr-2" />
                      Marcar como Não Lida
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Marcar como Lida
                    </>
                  )}
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleDeleteContact(selectedContact.id)}
                  className="flex-1"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Excluir
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminContacts;

